// Presentation parsing helpers for menu items.
//
// Each item carries a `presentations` JSON string with up to 4 fixed slots
// (porcion / individual / mediana / grande), plus a `unit_of_measure`.
// Every slot is { qty, price, active }. `price` is ABSOLUTE (not additive).
// "porcion" is the combo-eligible unit; larger slots are à la carte.

export const SLOT_ORDER = ['porcion', 'individual', 'mediana', 'grande'];

export const SLOT_LABELS = {
  porcion: 'Porción',
  individual: 'Individual',
  mediana: 'Mediana',
  grande: 'Grande',
};

export const COMBO_SLOT = 'porcion';

/**
 * Parse an item's `presentations` field into an ordered list of active options.
 * Accepts a JSON string or an already-parsed object. Returns [] when none.
 * @param {{ presentations?: string|object, unit_of_measure?: string }} item
 * @returns {Array<{ key: string, label: string, qty: number, unit: string, price: number, isComboUnit: boolean }>}
 */
export function parsePresentations(item) {
  if (!item) return [];

  let raw = item.presentations;
  if (!raw) return [];

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === '{}' || trimmed === '[]') return [];
    try {
      raw = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }

  if (!raw || typeof raw !== 'object') return [];

  const unit = item.unit_of_measure || '';

  return SLOT_ORDER.filter((key) => raw[key] && raw[key].active).map((key) => {
    const slot = raw[key];
    return {
      key,
      label: SLOT_LABELS[key] || key,
      qty: Number(slot.qty) || 0,
      unit,
      price: Number(slot.price) || 0,
      isComboUnit: key === COMBO_SLOT,
    };
  });
}

/**
 * True when an item exposes a real choice (2+ active presentations).
 * With fewer, the card shows a plain price and no selector.
 */
export function hasPresentationChoice(item) {
  return parsePresentations(item).length >= 2;
}

/**
 * Presentación protagonista de la card: la porción si está activa, y si no, la
 * de mayor cantidad.
 *
 * Los caldos se venden normalmente en su tamaño grande (1 litro) y el chico es
 * la alternativa "menos cantidad", así que tomar el primer slot del orden
 * anunciaba el más chico como si fuera el precio del platillo.
 * @returns {object|null}
 */
export function getPrincipalSize(list) {
  if (!list || list.length === 0) return null;
  const combo = list.find((p) => p.isComboUnit);
  if (combo) return combo;
  return list.reduce((mayor, p) => (p.qty > mayor.qty ? p : mayor), list[0]);
}

/**
 * Alias histórico de getPrincipalSize: el "tamaño seleccionado por defecto".
 * @returns {object|null}
 */
export function getDefaultPresentation(list) {
  return getPrincipalSize(list);
}

/**
 * Unidades de contador que consume vender `quantity` de una presentación.
 * Espejo de `_presentation_base_units` del backend: el contador se lleva en
 * unidades de la presentación protagonista, así que un grande de 800 g cuya
 * porción es de 200 g consume 4. Sin `presentationKey` —o con uno inactivo— se
 * asume la protagonista, que consume 1: el mismo fallback del backend, y el que
 * nunca sobrevende.
 *
 * El sitio tiene que hacer esta cuenta para no dejar pedir lo que no hay: el
 * backend rechaza la venta, pero aquí nadie está en el mostrador para avisarle
 * al cliente, y el pedido se cae hasta el final.
 *
 * @param {object} item producto con `presentations` + `unit_of_measure`
 * @param {number} quantity cuántas unidades de ESA presentación
 * @param {string|null} presentationKey slot vendido
 * @returns {number} unidades de contador, con 2 decimales
 */
export function baseUnits(item, quantity = 1, presentationKey = null) {
  const list = parsePresentations(item);
  if (list.length === 0) return quantity;

  const principal = getPrincipalSize(list);
  if (!principal || !principal.qty) return quantity;

  const elegida = (presentationKey && list.find((p) => p.key === presentationKey)) || principal;
  if (!elegida.qty) return quantity;

  return Math.round(quantity * (elegida.qty / principal.qty) * 100) / 100;
}

/**
 * Existencias para mostrar. El contador admite fracciones desde que los caldos
 * se cuentan en litros (medio litro descuenta 0.5), así que se muestra el
 * decimal sólo cuando existe: 10 se ve "10" y 9.5 se ve "9.5".
 */
export function formatStock(value) {
  const num = Number(value) || 0;
  return Number.isInteger(num) ? String(num) : num.toFixed(1);
}

/** Short "4 pza" label from qty + unit. Empty when no presentation. */
export function formatQtyUnit(presentation) {
  if (!presentation) return '';
  const { qty, unit } = presentation;
  if (!unit) return qty ? `${qty}` : '';
  return `${qty} ${unit}`;
}

/**
 * Human label for an amount in its unit of measure.
 * ml → "500 ml" / "1 L"; g → "500 g" / "1 kg"; pza/empty → "4 pzas".
 * @param {number} amount
 * @param {string} unit
 * @returns {string}
 */
export function formatAmount(amount, unit) {
  const n = Number(amount) || 0;
  if (unit === 'ml') return n >= 1000 ? `${n / 1000} L` : `${n} ml`;
  if (unit === 'g') return n >= 1000 ? `${n / 1000} kg` : `${n} g`;
  return `${n} ${n === 1 ? 'pza' : 'pzas'}`;
}

/**
 * Price per measure unit for value comparison.
 * pza → price per piece (/pza); ml/g → price per 100 (/100 ml, /100 g).
 * @param {{ qty: number, price: number }} size  qty = amount in `unit`
 * @param {string} unit
 * @returns {{ value: number, suffix: string }}
 */
export function perMeasure(size, unit) {
  const amount = Number(size?.qty) || 0;
  const price = Number(size?.price) || 0;
  if (amount <= 0) return { value: 0, suffix: unit === 'pza' || !unit ? '/pza' : `/100 ${unit}` };
  if (unit === 'pza' || !unit) return { value: price / amount, suffix: '/pza' };
  return { value: (price / amount) * 100, suffix: unit === 'ml' ? '/100 ml' : '/100 g' };
}

/**
 * Index of the best-value size (lowest price per measure) in a presentation list.
 * @param {Array<{qty:number, price:number}>} list
 * @param {string} unit
 * @returns {number} index, or -1 when empty
 */
export function bestSizeIdx(list, unit) {
  if (!list || list.length === 0) return -1;
  return list.reduce(
    (best, s, i) =>
      perMeasure(s, unit).value < perMeasure(list[best], unit).value ? i : best,
    0
  );
}
